import { ErrorBoundary } from '@sentry/react';
import { Suspense, useMemo } from 'react';

import Spinner from '@/components/Common/Spinner';
import { Button as DialogButton } from '@/shared/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import type { SharedLink, SharedLinkTargetType } from '../api/types';
import {
  buildSharedLinkUrl,
  expireSecondsToMode,
  generateSecurePassword,
} from '../lib';
import { type SharedLinkFormDefaults, sharedLinkFormOptions } from '../model';
import { SharedLinkApiBody } from './shared-link-api-body';
import { SharedLinkForm } from './shared-link-form';

function SharedLinkDialogBody({
  targetId,
  targetLink,
  sharedLinkData,
}: {
  targetId: string;
  targetLink: string;
  sharedLinkData: SharedLink;
}) {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const generatedPassword = useMemo(() => generateSecurePassword(), []);

  const initialValues = useMemo<SharedLinkFormDefaults>(() => {
    const { mode, enabled } = expireSecondsToMode(sharedLinkData.expire);

    return {
      workspaceId: currWorkspace?.alias ?? '',
      targetId,
      targetLink,
      sharedLinkId: sharedLinkData.id,
      sharedLink: buildSharedLinkUrl(sharedLinkData.id),
      isPublic: sharedLinkData.expire !== 0,
      linkExpireMode: mode,
      linkExpireEnabled: enabled,
      password: sharedLinkData.password ?? generatedPassword,
      passwordEnabled: !!sharedLinkData.password,
    };
  }, [
    currWorkspace?.alias,
    generatedPassword,
    sharedLinkData,
    targetId,
    targetLink,
  ]);

  const form = useAppForm({
    ...sharedLinkFormOptions,
    defaultValues: initialValues,
  });

  return <SharedLinkForm form={form} />;
}

type Props = {
  title: string;
  description: string;
  targetId: string;
  targetType: SharedLinkTargetType;
  targetLink: string;
  isOpen: boolean;
  close: () => void;
  unmount?: () => void;
};

export function SharedLinkDialog({
  title,
  description,
  targetId,
  targetType,
  targetLink,
  isOpen,
  close,
  unmount = () => undefined,
}: Props) {
  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="s">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody className="px-7 py-4">
          <ErrorBoundary fallback={<div>Unknown error</div>}>
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-8">
                  <Spinner size={32} />
                </div>
              }
            >
              <SharedLinkApiBody
                targetId={targetId}
                targetType={targetType}
                link={targetLink}
              >
                {(sharedLinkData) => (
                  <SharedLinkDialogBody
                    targetId={targetId}
                    targetLink={targetLink}
                    sharedLinkData={sharedLinkData}
                  />
                )}
              </SharedLinkApiBody>
            </Suspense>
          </ErrorBoundary>
        </DialogBody>
        <DialogFooter>
          <div />
          <DialogButton type="button" variant="outline" onClick={() => close()}>
            Close
          </DialogButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
